import { jobCanBeDownloaded } from '@helpers/jobStatus/jobCanBeDownloaded';
import { useAPI } from '@providers/useAPI';
import { useWebsocketContext } from '@providers/WebsocketProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AttemptDetailsResponseDto,
  AttemptSummaryDto,
  JobLicenseUsageHistoryResponseDto,
  JobPipelineStatusResponseDto,
  MetadataDto,
} from 'app/API';
import type { TAdminJobDetails } from '../types/TAdminJobDetails';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { cancelAndInvalidateQueries } from 'app/queries/cancelAndInvalidateQueries';
import {
  createContext,
  type Dispatch,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useParams } from 'react-router-dom';
import { createFetchAdminJobQueryFn } from '../api/fetchAdminJob';
import { useAdminJobDetailsSubscription } from '../hooks/useAdminJobDetailsSubscription';
import { createFetchAttemptDetailsQueryFn } from '../api/fetchAttemptDetails';
import { createFetchJobAttemptsQueryFn } from '../api/fetchJobAttempts';
import { createFetchJobEnhancementsQueryFn } from '../api/fetchJobEnhancements';
import { createFetchJobLicenseUsageHistoryQueryFn } from '../api/fetchJobLicenseUsageHistory';
import { createFetchJobPipelineStatusQueryFn } from '../api/fetchJobPipelineStatus';

type TState = {
  jobId: string;
  isLoading: boolean;
  job?: TAdminJobDetails;
  setJob: Dispatch<SetStateAction<TAdminJobDetails | undefined>>;
  showMaterials: boolean;
  showBackupFiles: boolean;
  showExhibitFiles: boolean;
  showReporterNotes: boolean;
  showSourceFiles: boolean;
  showSchedule: boolean;
  showDownloads: boolean;
  manualReload: () => void;
  metadata: MetadataDto | null;
  pipelineStatus: JobPipelineStatusResponseDto | null;
  attempts: AttemptSummaryDto[];
  selectedAttemptId: string | null;
  attemptDetails: AttemptDetailsResponseDto | null;
  pipelineLoading: boolean;
  attemptLoading: boolean;
  pipelineError: string | null;
  licenseUsageHistory: JobLicenseUsageHistoryResponseDto[];
  loadPipeline: (options?: { focusLatest?: boolean }) => Promise<null | string | undefined | void>;
  loadAttemptDetails: (attemptId: string, options?: { silent?: boolean; preserveData?: boolean }) => Promise<void>;
  setSelectedAttemptId: (attemptId: string | null) => void;
  refreshPipelineStatus: () => Promise<void>;
  manualRefreshJobData: () => Promise<void>;
};

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return Number.NEGATIVE_INFINITY;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const resolveAttemptTimestamp = (attempt?: AttemptSummaryDto | null) => {
  if (!attempt) {
    return Number.NEGATIVE_INFINITY;
  }
  return Math.max(toTimestamp(attempt.startedAt), toTimestamp(attempt.completedAt));
};

const findLatestAttemptId = (attemptList?: AttemptSummaryDto[] | null) => {
  if (!attemptList?.length) {
    return null;
  }
  let latestAttemptId = attemptList[0]?.attemptId ?? null;
  let latestTimestamp = resolveAttemptTimestamp(attemptList[0]);

  for (let index = 1; index < attemptList.length; index += 1) {
    const attempt = attemptList[index];
    const candidateTimestamp = resolveAttemptTimestamp(attempt);
    if (candidateTimestamp > latestTimestamp) {
      latestTimestamp = candidateTimestamp;
      latestAttemptId = attempt.attemptId;
    }
  }

  return latestAttemptId;
};

const PageContext = createContext<TState>({
  jobId: '',
  isLoading: false,
  job: undefined,
  setJob: () => {},
  showMaterials: false,
  showBackupFiles: false,
  showExhibitFiles: false,
  showReporterNotes: false,
  showSourceFiles: false,
  showSchedule: false,
  showDownloads: false,
  manualReload: () => {},
  metadata: null,
  pipelineStatus: null,
  attempts: [],
  selectedAttemptId: null,
  attemptDetails: null,
  pipelineLoading: false,
  attemptLoading: false,
  pipelineError: null,
  licenseUsageHistory: [],
  loadPipeline: async () => {},
  loadAttemptDetails: async () => {},
  setSelectedAttemptId: () => {},
  refreshPipelineStatus: async () => {},
  manualRefreshJobData: async () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { websocketSessionId } = useWebsocketContext();
  const params = useParams() as { id: string };
  const jobId = params?.id ?? '';

  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);
  const selectedAttemptIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedAttemptIdRef.current = selectedAttemptId;
  }, [selectedAttemptId]);

  const jobQuery = useQuery({
    queryKey: adminJobsKeys.detail(jobId),
    queryFn: createFetchAdminJobQueryFn(call),
    enabled: jobId !== '',
  });
  const enhancementsQuery = useQuery({
    queryKey: adminJobsKeys.enhancementsByJob(jobId),
    queryFn: createFetchJobEnhancementsQueryFn(call),
    enabled: jobId !== '',
  });
  const licenseUsageQuery = useQuery({
    queryKey: adminJobsKeys.licenseUsageByJob(jobId),
    queryFn: createFetchJobLicenseUsageHistoryQueryFn(call),
    enabled: jobId !== '',
  });
  const pipelineStatusQuery = useQuery({
    queryKey: adminJobsKeys.pipelineStatusByJob(jobId),
    queryFn: createFetchJobPipelineStatusQueryFn(call),
    enabled: jobId !== '',
  });
  const attemptsQuery = useQuery({
    queryKey: adminJobsKeys.attemptsByJob(jobId),
    queryFn: createFetchJobAttemptsQueryFn(call),
    enabled: jobId !== '',
  });
  const attemptDetailsQuery = useQuery({
    queryKey: adminJobsKeys.attemptDetail(selectedAttemptId ?? ''),
    queryFn: createFetchAttemptDetailsQueryFn(call),
    enabled: selectedAttemptId !== null && selectedAttemptId !== '',
  });

  const job = jobQuery.data;
  const metadata = enhancementsQuery.data?.metadata ?? null;
  const licenseUsageHistory = licenseUsageQuery.data ?? [];
  const pipelineStatus = pipelineStatusQuery.data ?? null;
  const attempts = attemptsQuery.data ?? [];
  const attemptDetails = selectedAttemptId ? attemptDetailsQuery.data ?? null : null;

  const isLoading = jobQuery.isFetching || enhancementsQuery.isFetching || licenseUsageQuery.isFetching;
  const pipelineLoading = pipelineStatusQuery.isFetching || attemptsQuery.isFetching;
  const attemptLoading = selectedAttemptId !== null && attemptDetailsQuery.isLoading;
  const pipelineError =
    pipelineStatusQuery.isError || attemptsQuery.isError
      ? 'Unable to load pipeline data.'
      : selectedAttemptId && attemptDetailsQuery.isError
        ? 'Unable to load attempt details.'
        : null;

  useEffect(() => {
    const attemptList = attemptsQuery.data;
    if (!attemptList) {
      return;
    }

    const latestAttemptId = findLatestAttemptId(attemptList);
    const fallbackAttemptId =
      latestAttemptId ??
      pipelineStatusQuery.data?.currentAttemptId ??
      attemptList[0]?.attemptId ??
      pipelineStatusQuery.data?.previousAttempts?.[0]?.attemptId ??
      null;

    const currentSelectedAttemptId = selectedAttemptIdRef.current;
    const selectedAttemptStillExists =
      !!currentSelectedAttemptId && attemptList.some((attempt) => attempt.attemptId === currentSelectedAttemptId);
    const resolvedAttemptId = selectedAttemptStillExists ? currentSelectedAttemptId : fallbackAttemptId;

    if (resolvedAttemptId !== currentSelectedAttemptId) {
      setSelectedAttemptId(resolvedAttemptId);
    }
  }, [attemptsQuery.data, pipelineStatusQuery.data]);

  const setJob: Dispatch<SetStateAction<TAdminJobDetails | undefined>> = (action) => {
    queryClient.setQueryData<TAdminJobDetails | undefined>(adminJobsKeys.detail(jobId), (currentJob) =>
      typeof action === 'function' ? action(currentJob) : action,
    );
  };

  const refreshJobData = () =>
    Promise.all([
      cancelAndInvalidateQueries(queryClient, adminJobsKeys.detail(jobId)),
      cancelAndInvalidateQueries(queryClient, adminJobsKeys.enhancementsByJob(jobId)),
      cancelAndInvalidateQueries(queryClient, adminJobsKeys.licenseUsageByJob(jobId)),
    ]);

  const loadPipeline = async (options?: { focusLatest?: boolean }) => {
    if (!jobId) {
      return;
    }
    await Promise.all([
      queryClient.cancelQueries({ queryKey: adminJobsKeys.pipelineStatusByJob(jobId) }),
      queryClient.cancelQueries({ queryKey: adminJobsKeys.attemptsByJob(jobId) }),
    ]);
    const [statusResult, attemptsResult] = await Promise.all([pipelineStatusQuery.refetch(), attemptsQuery.refetch()]);
    await cancelAndInvalidateQueries(queryClient, adminJobsKeys.attemptDetails());
    const attemptList = attemptsResult.data ?? [];
    const latestAttemptId = findLatestAttemptId(attemptList);
    if (options?.focusLatest) {
      const fallbackAttemptId =
        latestAttemptId ??
        statusResult.data?.currentAttemptId ??
        attemptList[0]?.attemptId ??
        statusResult.data?.previousAttempts?.[0]?.attemptId ??
        null;
      setSelectedAttemptId(fallbackAttemptId);
    }
    return latestAttemptId;
  };

  const loadAttemptDetails = async (attemptId: string) => {
    if (!attemptId) {
      return;
    }
    if (selectedAttemptIdRef.current === attemptId) {
      await cancelAndInvalidateQueries(queryClient, adminJobsKeys.attemptDetail(attemptId));
    } else {
      setSelectedAttemptId(attemptId);
    }
  };

  const refreshPipelineStatus = async () => {
    if (!jobId) {
      return;
    }
    await cancelAndInvalidateQueries(queryClient, adminJobsKeys.pipelineStatusByJob(jobId));
    const currentSelectedAttemptId = selectedAttemptIdRef.current;
    if (currentSelectedAttemptId) {
      await cancelAndInvalidateQueries(queryClient, adminJobsKeys.attemptDetail(currentSelectedAttemptId));
    }
  };

  const manualReload = () => {
    void refreshJobData();
    void loadPipeline({ focusLatest: true });
  };

  const manualRefreshJobData = async () => {
    await Promise.all([refreshJobData(), loadPipeline()]);
  };

  const showBackupFiles = !!job?.backupFiles?.length;
  const showExhibitFiles = !!job?.exhibitFiles?.length;
  const showDownloads = !!(job?.status && job?.results?.length && jobCanBeDownloaded(job?.status));
  const showReporterNotes = !!job?.reporterNotes?.length;
  const showSourceFiles = !!job?.sourceFiles?.length || !!job?.media?.length;
  const showAttachments = !!job?.attachments?.length;
  const showMaterials = !!(
    job?.notes ||
    showDownloads ||
    showBackupFiles ||
    showExhibitFiles ||
    showReporterNotes ||
    showSourceFiles ||
    showAttachments
  );
  const showSchedule = !!job?.meetingType;

  useAdminJobDetailsSubscription(websocketSessionId, jobId);

  return (
    <PageContext.Provider
      value={{
        jobId,
        isLoading,
        job,
        setJob,
        showMaterials,
        showBackupFiles,
        showExhibitFiles,
        showReporterNotes,
        showSourceFiles,
        showSchedule,
        showDownloads,
        manualReload,
        metadata,
        pipelineStatus,
        attempts,
        selectedAttemptId,
        attemptDetails,
        pipelineLoading,
        attemptLoading,
        pipelineError,
        licenseUsageHistory,
        loadPipeline,
        loadAttemptDetails,
        setSelectedAttemptId,
        refreshPipelineStatus,
        manualRefreshJobData,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
